<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import PipelineAPI from '../../../../../../api/pipelineCards';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['navigate-to-card']);

const { t, locale } = useI18n();
const store = useStore();

const loading = ref(false);
const relatedCards = ref([]);
const error = ref(null);
const filterStatus = ref('all');

const getUserLocale = () => {
  return (locale.value || navigator.language || 'en-US').replace('_', '-');
};

const filteredCards = computed(() => {
  if (filterStatus.value === 'all') return relatedCards.value;
  return relatedCards.value.filter(c => {
    if (filterStatus.value === 'won') return !!c.won_at;
    if (filterStatus.value === 'lost') return !!c.lost_at;
    if (filterStatus.value === 'open') return !c.won_at && !c.lost_at;
    return true;
  });
});

const statusBadgeClass = card => {
  if (card.won_at) return 'bg-grass-3 text-grass-11';
  if (card.lost_at) return 'bg-ruby-3 text-ruby-11';
  return 'bg-n-alpha-1 text-n-slate-11';
};

const statusLabel = card => {
  if (card.won_at) return t('PIPELINE.CARD.STATUS.WON');
  if (card.lost_at) return t('PIPELINE.CARD.STATUS.LOST');
  return t('PIPELINE.CARD.STATUS.OPEN');
};

const resolveStageName = stageId => {
  if (!stageId) return '-';
  const currentPipeline = store.getters['pipelines/getSelectedPipeline'];
  if (!currentPipeline?.stages) return stageId;
  return currentPipeline.stages[stageId]?.name || stageId;
};

const formatCurrency = value => {
  if (!value) return '';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return '';
  try {
    return new Intl.NumberFormat(getUserLocale(), {
      style: 'currency',
      currency: 'BRL',
    }).format(num);
  } catch {
    return `${value}`;
  }
};

const formatRelativeTime = dateStr => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}m`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d`;
  } catch {
    return '';
  }
};

const fetchRelatedCards = async () => {
  if (!props.item.contact_id) {
    relatedCards.value = [];
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    const { data } = await PipelineAPI.getCards(null, {
      contact_id: props.item.contact_id,
      exclude_id: props.item.id,
      limit: 50,
    });
    // Handle both paginated (data.data) and legacy (data as array) responses
    if (data?.data && Array.isArray(data.data)) {
      relatedCards.value = data.data;
    } else if (Array.isArray(data)) {
      relatedCards.value = data;
    } else {
      relatedCards.value = [];
    }
  } catch {
    error.value = t('PIPELINE.RELATED.LOAD_ERROR');
    relatedCards.value = [];
  } finally {
    loading.value = false;
  }
};

onMounted(fetchRelatedCards);
</script>

<template>
  <div class="space-y-4">
    <!-- Header -->
    <div class="flex items-center justify-between gap-2 flex-wrap">
      <h3 class="text-lg font-semibold text-n-slate-12">
        {{ t('PIPELINE.RELATED.TITLE') }}
        <span
          v-if="relatedCards.length"
          class="text-sm font-normal text-n-slate-11"
        >
          ({{ relatedCards.length }})
        </span>
      </h3>
      <!-- Status filter -->
      <select
        v-model="filterStatus"
        class="text-xs px-2 py-1 rounded border border-n-weak bg-n-solid-2 text-n-slate-11 focus:outline-none focus:border-n-brand"
      >
        <option value="all">{{ t('PIPELINE.RELATED.FILTER_ALL') }}</option>
        <option value="open">{{ t('PIPELINE.CARD.STATUS.OPEN') }}</option>
        <option value="won">{{ t('PIPELINE.CARD.STATUS.WON') }}</option>
        <option value="lost">{{ t('PIPELINE.CARD.STATUS.LOST') }}</option>
      </select>
    </div>

    <!-- Error state -->
    <div v-if="error" class="text-center py-8">
      <p class="text-sm text-ruby-11">{{ error }}</p>
    </div>

    <!-- Loading state -->
    <div v-else-if="loading" class="text-center py-12">
      <span
        class="inline-block w-8 h-8 border-2 border-n-weak border-t-n-brand rounded-full animate-spin"
      />
    </div>

    <!-- Empty state -->
    <div v-else-if="!filteredCards.length" class="text-center py-12">
      <fluent-icon
        icon="folder-open"
        size="32"
        class="text-n-slate-9 mx-auto"
      />
      <p class="text-sm text-n-slate-10 mt-2">
        {{ t('PIPELINE.RELATED.EMPTY_STATE') }}
      </p>
    </div>

    <!-- Cards list -->
    <div v-else class="space-y-2">
      <div
        v-for="card in filteredCards"
        :key="card.id"
        class="bg-n-solid-3 rounded-lg p-4 hover:bg-n-alpha-1 cursor-pointer transition-colors"
        @click="emit('navigate-to-card', card.id)"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1 min-w-0">
            <!-- Title + status badge -->
            <div class="flex items-center gap-2 mb-1">
              <h4 class="font-medium text-n-slate-12 truncate">
                {{ card.title || t('PIPELINE.UNTITLED_ITEM') }}
              </h4>
              <span
                class="flex-shrink-0 text-xs px-2 py-0.5 rounded-full"
                :class="statusBadgeClass(card)"
              >
                {{ statusLabel(card) }}
              </span>
            </div>

            <!-- Pipeline / stage / value -->
            <div
              class="flex items-center gap-1.5 text-xs text-n-slate-11 flex-wrap"
            >
              <span>{{ card.pipeline?.name || '—' }}</span>
              <span>•</span>
              <span>{{ resolveStageName(card.pipeline_stage) }}</span>
              <template v-if="card.item_details?.value">
                <span>•</span>
                <span>{{ formatCurrency(card.item_details.value) }}</span>
              </template>
            </div>

            <!-- Owner + relative time -->
            <div class="flex items-center gap-2 mt-2 text-xs text-n-slate-10">
              <Avatar
                :name="card.owner?.name || t('PIPELINE.RELATED.NO_AGENT')"
                :src="card.owner?.avatar_url"
                :size="20"
              />
              <span>{{
                card.owner?.name || t('PIPELINE.RELATED.NO_AGENT')
              }}</span>
              <span>•</span>
              <span>{{ formatRelativeTime(card.updated_at) }}</span>
            </div>
          </div>

          <!-- View button -->
          <button
            class="flex-shrink-0 px-3 py-1 text-xs font-medium bg-n-brand text-white rounded hover:brightness-110 transition-all"
            @click.stop="emit('navigate-to-card', card.id)"
          >
            {{ t('PIPELINE.RELATED.VIEW_ITEM') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
